import './DropdownModal.scss';

import classNames from 'classnames';
import { OutsideClickWrapper } from '@ui/general';
import { PopoverPosition } from './PopoverModal';

import { getClosestButton, getClosestViewArea } from '@lib/popups';
import React, { useCallback, useEffect, useState } from 'react';
import { SonarModal, SonarModalProps } from './SonarModal';

function getContainerOffset(
	element: HTMLElement,
	container: HTMLElement,
	direction: 'top' | 'left' = 'top'
): number {
	let currentElement: HTMLElement | null = element;

	if (!currentElement) {
	  return 0;
	}

	let totalOffset: number = 0;
	let scrollAdjustment: number = 0;

	while (currentElement !== container) {
	  totalOffset += direction === 'top' ? currentElement.offsetTop : currentElement.offsetLeft;

	  if (currentElement.offsetParent) {
			const offsetParent = currentElement.offsetParent as HTMLElement;
			const scrollValue = direction === 'top' ? offsetParent.scrollTop : offsetParent.scrollLeft;
			if (scrollValue > 0) {
		  scrollAdjustment += scrollValue;
			}
	  }

	  currentElement = currentElement.offsetParent as HTMLElement | null;

	  if (!currentElement) {
			break;
	  }
	}

	const containerScroll = direction === 'top' ? container.scrollTop : container.scrollLeft;
	if (containerScroll > 0) {
	  scrollAdjustment += containerScroll;
	}

	return totalOffset - scrollAdjustment;
}

function calculatePopoverPosition(
	position: PopoverPosition,
	triggerButton: HTMLElement,
	container: HTMLElement
): React.CSSProperties {
	const triggerRect = triggerButton.getBoundingClientRect();
	const containerRect = container.getBoundingClientRect();
	const topOffset = getContainerOffset(triggerButton, container);

	if (position === 'bottomLeft') {
		return {
			left: triggerRect.left - containerRect.left,
			top: topOffset - container.scrollHeight - triggerRect.height + 8
		};
	}

	if (position === 'bottomRight') {
		return {
			right: -triggerRect.width - (triggerRect.left - containerRect.left),
			top: topOffset - container.scrollHeight - triggerRect.height + 8
		};
	}

	if (position === 'topLeft') {
		return {
			bottom: container.scrollHeight - topOffset + triggerRect.height + 1,
			left: triggerRect.left - containerRect.left
		};
	}

	if (position === 'topRight') {
		return {
			bottom: container.scrollHeight - topOffset + triggerRect.height + 1,
			right: -triggerRect.width - (triggerRect.left - containerRect.left)
		};
	}

	return {};
}

export interface DropdownModalProps extends SonarModalProps {
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	onClickOutside?: (event: MouseEvent) => void;
	onCancel?: () => void;
	onOk?: () => void;
	popoverPosition?: PopoverPosition;
	targetNode?: HTMLElement | null;
}

export const DropdownModal: React.FC<DropdownModalProps> = ({
	className,
	getPopupContainer = getClosestViewArea,
	onCancel,
	onClickOutside,
	onOk,
	popoverPosition = 'bottomLeft',
	style,
	targetNode,
	...rest
}) => {
	const [ open, setOpen ] = useState(false);
	const [ onCloseAction, setOnCloseAction ] = useState<() => void>();

	const handleClosing = useCallback((action?: () => void) => {
		if (open) {
			setOnCloseAction(() => action);
			setOpen(false);
		}
	}, [ open ]);

	const onAfterClose = useCallback(() => {
		onCloseAction?.();
		setOnCloseAction(undefined);
	}, [ onCloseAction ]);

	const handleOnClickOutside = useCallback((e: MouseEvent) => {
		if (targetNode !== getClosestButton(e.target as HTMLElement)) {
			if (onClickOutside) {
				onClickOutside(e);
			} else {
				handleClosing();
			}
		}
	}, [ handleClosing, onClickOutside, targetNode ]);

	const [ customStyle, setCustomStyle ] = useState<React.CSSProperties>(style ?? {});

	useEffect(() => {
		const handleClick = () => setOpen((value) => !value);

		targetNode?.addEventListener('click', handleClick);
		targetNode?.click();

		return () => {
		  targetNode?.removeEventListener('click', handleClick);
		};
	}, [ targetNode ]);

	useEffect(() => {
		if (targetNode) {
			setCustomStyle((current) => ({
				...current,
				...calculatePopoverPosition(
					popoverPosition,
					getClosestButton(targetNode) ?? targetNode,
					getClosestViewArea(targetNode as HTMLElement ?? document.body)
				)
			}));
		}
	}, [ open, popoverPosition, targetNode ]);

	const modalClassNames = classNames(
		'sonar-modal-dropdown',
		{
			'sonar-modal-dropdown-bottom-left': popoverPosition === 'bottomLeft',
			'sonar-modal-dropdown-bottom-right': popoverPosition === 'bottomRight',
			'sonar-modal-dropdown-top-left': popoverPosition === 'topLeft',
			'sonar-modal-dropdown-top-right': popoverPosition === 'topRight'
		},
		'flex-inputs',
		className
	);

	return (
		<OutsideClickWrapper onClickOutside={ handleOnClickOutside }>
			<SonarModal
				{ ...rest }
				afterClose={ onAfterClose }
				className={ classNames('sonar-modal-dropdown', modalClassNames, className) }
				getContainer={ () => getPopupContainer(targetNode as HTMLElement ?? document.body) }
				onCancel={ () => handleClosing(onCancel) }
				onOk={ () => handleClosing(onOk) }
				open={ open }
				rootClassName='sonar-modal-dropdown-root'
				style={ customStyle } />
		</OutsideClickWrapper>
	);
};
